import { JsonValue, getOwn, setOwn } from "../json";

/**
 * The operator family a scalar belongs to. Conditions, the variable picker and
 * the path explorer pick behavior from the base, so a new scalar (Email,
 * Money) only has to say which existing family it behaves like.
 */
export type ScalarBase =
  | "string"
  | "number"
  | "boolean"
  | "datetime"
  | "date"
  | "json";

export type TypeRef =
  | { kind: "named"; name: string; nullable: boolean }
  | { kind: "list"; of: TypeRef; nullable: boolean };

export interface ScalarType {
  kind: "scalar";
  name: string;
  description: string;
  base: ScalarBase;
  /**
   * For entity ids: the entity this identifies. Entity ids only accept
   * their own type (a ticket id can't target a fulfillment), and widen to
   * ID and String.
   */
  entity?: string;
}

export interface EnumValue {
  value: string;
  label: string;
  description?: string;
}

export interface EnumType {
  kind: "enum";
  name: string;
  description: string;
  values: EnumValue[];
}

export interface ArgDef {
  type: TypeRef;
  description: string;
}

export interface FieldVisibility {
  /** Hidden from the condition builder's variable picker. */
  conditions?: boolean;
  /** Hidden from template/variable insertion. */
  variables?: boolean;
}

export interface FieldDef {
  type: TypeRef;
  description: string;
  label?: string;
  args?: Record<string, ArgDef>;
  /**
   * Set when the field is not part of the eagerly provided payload and must
   * be fetched. Names a resolver the host supplies (e.g. a CRDB or service
   * lookup). Fields without a resolver are read straight off the parent
   * value.
   */
  resolver?: string;
  /**
   * Value used when the resolver fails. Makes fail-open/fail-closed explicit
   * per field.
   */
  fallback?: JsonValue;
  /**
   * "fail-run": a failed lookup fails the step (and, after the host's
   * retries, the run) instead of using the fallback.
   */
  onFailure?: "fallback" | "fail-run";
  hidden?: FieldVisibility;
  deprecationReason?: string;
}

export interface ObjectType {
  kind: "object";
  name: string;
  description: string;
  fields: Record<string, FieldDef>;
}

export type NamedType = ScalarType | EnumType | ObjectType;

export interface TypeSystem {
  types: Record<string, NamedType>;
}

export interface PathSegment {
  field: string;
  args?: Record<string, JsonValue>;
}

export type Path = PathSegment[];

export const BUILTIN_SCALARS: ScalarType[] = [
  { kind: "scalar", name: "String", description: "Text", base: "string" },
  { kind: "scalar", name: "Int", description: "Whole number", base: "number" },
  {
    kind: "scalar",
    name: "Float",
    description: "Decimal number",
    base: "number",
  },
  {
    kind: "scalar",
    name: "Boolean",
    description: "True or false",
    base: "boolean",
  },
  { kind: "scalar", name: "ID", description: "Identifier", base: "string" },
  {
    kind: "scalar",
    name: "DateTime",
    description: "ISO-8601 timestamp",
    base: "datetime",
  },
  {
    kind: "scalar",
    name: "Date",
    description: "Calendar date (YYYY-MM-DD)",
    base: "date",
  },
  { kind: "scalar", name: "JSON", description: "Arbitrary JSON", base: "json" },
];

export function named(name: string, nullable = true): TypeRef {
  return { kind: "named", name, nullable };
}

export function nonNull(name: string): TypeRef {
  return { kind: "named", name, nullable: false };
}

export function listOf(of: TypeRef, nullable = true): TypeRef {
  return { kind: "list", of, nullable };
}

export function withNullable(type: TypeRef, nullable: boolean): TypeRef {
  return { ...type, nullable };
}

export function createTypeSystem(...parts: NamedType[][]): TypeSystem {
  const types: Record<string, NamedType> = {};
  for (const scalar of BUILTIN_SCALARS) {
    setOwn(types, scalar.name, scalar);
  }
  for (const part of parts) {
    for (const type of part) {
      const existing = getOwn(types, type.name);
      if (existing && !sameDefinition(existing, type)) {
        throw new Error(`Conflicting definitions for type ${type.name}`);
      }
      setOwn(types, type.name, type);
    }
  }
  return { types };
}

export function extendTypeSystem(
  base: TypeSystem,
  ...parts: NamedType[][]
): TypeSystem {
  return createTypeSystem(Object.values(base.types), ...parts);
}

function sameDefinition(a: NamedType, b: NamedType): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function findType(ts: TypeSystem, name: string): NamedType | undefined {
  return getOwn(ts.types, name);
}

export function getNamedType(ts: TypeSystem, name: string): NamedType {
  const type = findType(ts, name);
  if (!type) {
    throw new Error(`Unknown type ${name}`);
  }
  return type;
}

/** The innermost named type, skipping list wrappers. */
export function unwrapNamed(type: TypeRef): {
  name: string;
  listDepth: number;
} {
  let depth = 0;
  let current = type;
  while (current.kind === "list") {
    depth += 1;
    current = current.of;
  }
  return { name: current.name, listDepth: depth };
}

export function typeToString(type: TypeRef): string {
  const inner = type.kind === "list" ? `[${typeToString(type.of)}]` : type.name;
  return type.nullable ? inner : `${inner}!`;
}

export function scalarBase(
  ts: TypeSystem,
  type: TypeRef,
): ScalarBase | "enum" | "object" | "list" {
  if (type.kind === "list") {
    return "list";
  }
  const namedType = getNamedType(ts, type.name);
  switch (namedType.kind) {
    case "scalar":
      return namedType.base;
    case "enum":
      return "enum";
    case "object":
      return "object";
  }
}

export type PathResolution =
  | { ok: true; type: TypeRef; lazySegments: number[] }
  | { ok: false; error: string; failedAt: number };

/**
 * Resolves the static type of a path from a root object type. A nullable
 * parent makes everything below it nullable; a list parent maps the rest of
 * the path over the elements (GraphQL semantics), and a nullable element
 * reads as null.
 */
export function resolvePathType(
  ts: TypeSystem,
  root: TypeRef,
  path: Path,
): PathResolution {
  let current = root;
  // Whether the value at the innermost list level (or the top) may be null.
  let nullable = root.nullable;
  const listLevels: boolean[] = [];
  const lazySegments: number[] = [];
  for (const [i, segment] of path.entries()) {
    if (!segment) {
      return { ok: false, error: "Empty path segment", failedAt: i };
    }
    while (current.kind === "list") {
      listLevels.push(nullable);
      nullable = current.of.nullable;
      current = current.of;
    }
    const name = current.name;
    const namedType = findType(ts, name);
    if (namedType?.kind === "scalar" && namedType.base === "json") {
      // Untyped data: anything below it is JSON that may be missing.
      const json = listLevels.reduce<TypeRef>(
        (inner) => listOf(inner, true),
        named("JSON", true),
      );
      return { ok: true, type: json, lazySegments };
    }
    const field =
      namedType?.kind === "object"
        ? getOwn(namedType.fields, segment.field)
        : undefined;
    if (!field) {
      return {
        ok: false,
        error: `${name} has no field "${segment.field}"`,
        failedAt: i,
      };
    }
    const argError = checkArgs(field, segment);
    if (argError) {
      return { ok: false, error: argError, failedAt: i };
    }
    if (field.resolver) {
      lazySegments.push(i);
    }
    nullable = nullable || field.type.nullable;
    current = field.type;
  }
  let result: TypeRef = withNullable(current, nullable);
  for (let i = listLevels.length - 1; i >= 0; i -= 1) {
    result = listOf(result, listLevels[i] ?? true);
  }
  return { ok: true, type: result, lazySegments };
}

function checkArgs(field: FieldDef, segment: PathSegment): string | undefined {
  const declared = field.args ?? {};
  const given = segment.args ?? {};
  for (const key of Object.keys(given)) {
    if (!getOwn(declared, key)) {
      return `Unknown argument "${key}"`;
    }
  }
  for (const [key, arg] of Object.entries(declared)) {
    if (!arg.type.nullable && getOwn(given, key) === undefined) {
      return `Missing required argument "${key}"`;
    }
  }
  return undefined;
}

export function pathToString(path: Path): string {
  return path
    .map((s) => {
      if (!s.args || Object.keys(s.args).length === 0) {
        return s.field;
      }
      const args = Object.entries(s.args)
        .map(([k, v]) => `${k}: ${JSON.stringify(v)}`)
        .join(", ");
      return `${s.field}(${args})`;
    })
    .join(".");
}

export function pathFromString(text: string): Path {
  return text
    .split(".")
    .filter((s) => s.length > 0)
    .map((field) => ({ field }));
}

/**
 * Whether a value of `actual` can be used where `expected` is required.
 * Nullability is checked by the caller where it matters (optional inputs
 * accept nullable values; required ones are flagged as warnings).
 */
export function isAssignable(
  ts: TypeSystem,
  actual: TypeRef,
  expected: TypeRef,
): boolean {
  if (expected.kind === "list") {
    // Untyped data may hold a list; it's checked when it runs.
    if (actual.kind === "named" && actual.name === "JSON") {
      return true;
    }
    return actual.kind === "list" && isAssignable(ts, actual.of, expected.of);
  }
  if (actual.kind === "list") {
    return expected.name === "JSON";
  }
  // Untyped data (JSON, null literals) is dynamic: usable anywhere, checked when it runs.
  if (
    expected.name === actual.name ||
    expected.name === "JSON" ||
    actual.name === "JSON"
  ) {
    return true;
  }
  const expectedType = findType(ts, expected.name);
  const actualType = findType(ts, actual.name);
  if (expectedType?.kind === "scalar" && actualType?.kind === "scalar") {
    if (expectedType.name === "String") {
      // Dates and timestamps are ISO text.
      return (
        actualType.base === "string" ||
        actualType.base === "date" ||
        actualType.base === "datetime"
      );
    }
    if (expectedType.name === "Float") {
      return actualType.base === "number";
    }
    if (expectedType.name === "ID") {
      return actualType.entity !== undefined;
    }
  }
  if (
    expectedType?.kind === "scalar" &&
    expectedType.name === "String" &&
    actualType?.kind === "enum"
  ) {
    return true;
  }
  return false;
}
